import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronLeft, ChevronRight, Truck, Plus, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalTime } from '@/utils/formatLocalTime'; // 🆕 [P1 SUITE+]
import { AlertesConflitsLocation } from '@/components/location/AlertesConflitsLocation'; // 🆕 [Phase v13.60.3 — 2026-08-12]

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const MOIS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
const JOURS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

export function CalendrierEquipements() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [equipements, setEquipements] = useState([]);
  const [occupations, setOccupations] = useState({});
  const [locationsDates, setLocationsDates] = useState({});
  const [locationsRaw, setLocationsRaw] = useState([]);
  const [etagesUtilises, setEtagesUtilises] = useState({});
  const [retardsProbables, setRetardsProbables] = useState({});
  const [occupationDetails, setOccupationDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [locForm, setLocForm] = useState({ equipement_id: '', date_debut: '', date_fin: '', client_nom: '', note: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eqRes, occRes, locRes] = await Promise.all([
        axios.get(`${API_URL}/api/equipements-inventaire`),
        axios.get(`${API_URL}/api/crm/equipements-occupes`),
        axios.get(`${API_URL}/api/locations-manuelles`),
      ]);
      if (eqRes.status === 200) { const d = eqRes.data; setEquipements((d.equipements || []).filter(e => e.actif)); }
      if (occRes.status === 200) {
        const d = occRes.data;
        setOccupations(d.occupations || {});
        setLocationsDates(d.locations_manuelles || {});
        setEtagesUtilises(d.etages_utilises || {});
        setRetardsProbables(d.retards_probables || {});
        setOccupationDetails(d.occupation_details || {});
      }
      if (locRes.status === 200) { const d = locRes.data; setLocationsRaw(d.locations || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay();

  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const toDateStr = (day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const todayStr = new Date().toISOString().split('T')[0];

  const getEqStatus = (eqId, dateStr) => {
    const isOccupied = (occupations[eqId] || []).includes(dateStr);
    const isRented = (locationsDates[eqId] || []).includes(dateStr);
    if (isOccupied) return 'production';
    if (isRented) return 'loue';
    return 'dispo';
  };

  const handleCreateLocation = async () => {
    if (!locForm.equipement_id || !locForm.date_debut || !locForm.date_fin) { toast.error('Equipement et dates requis'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/locations-manuelles`, locForm);
      if (res.status === 200) { toast.success('Location ajoutee'); setShowAddDialog(false); fetchData(); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleDeleteLocation = async (locId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/locations-manuelles/${locId}`);
      if (res.status === 200) { toast.success('Location supprimee'); fetchData(); }
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="p-6 text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" /></div>;

  const STATUS_COLORS = { dispo: '#22c55e', production: '#ef4444', loue: '#f59e0b' };
  const retardsList = Object.entries(retardsProbables || {}).flatMap(([eqId, arr]) =>
    (arr || []).map(r => ({ eqId, ...r }))
  );
  const eqNameById = Object.fromEntries(equipements.map(e => [e.id, `${e.emoji || ''} ${e.nom}`]));

  return (
    <div className="p-4 sm:p-6" data-testid="calendrier-equipements">
      {/* 🆕 [Phase v13.60.3 — 2026-08-12] Alertes non-bloquantes de conflits de location.
          Rôles autorisés (Owner / adjoint_admin / charge_projet) → composant s'auto-cache pour les autres. */}
      <AlertesConflitsLocation variant="card" />

      {retardsList.length > 0 && (
        <div
          className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-md p-3"
          data-testid="calendrier-retards-probables"
        >
          <p className="text-sm font-semibold text-amber-900 mb-1">
            ⚠️ {retardsList.length} équipement(s) en retard probable
          </p>
          <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-auto">
            {retardsList.slice(0, 8).map((r, idx) => (
              <li key={idx}>
                • {eqNameById[r.eqId] || 'Équipement'} — {r.dossier_nom} (prévu jusqu'au {r.derniere_date_prevue}, +{r.nb_jours_retard}j)
              </li>
            ))}
            {retardsList.length > 8 && <li>… et {retardsList.length - 8} autre(s)</li>}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendrier de Location</h1>
          <p className="text-sm text-gray-500">Vue unifiee — toutes les nacelles sur un seul calendrier</p>
        </div>
        <Button onClick={() => { setShowAddDialog(true); setLocForm({ equipement_id: '', date_debut: selectedDay || '', date_fin: selectedDay || '', client_nom: '', note: '' }); }} data-testid="btn-add-location">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle location
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4 pb-2">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="text-lg font-bold">{MOIS[month]} {year}</h2>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
          </div>

          {/* Legende */}
          <div className="flex flex-wrap gap-4 mb-3 text-xs">
            {equipements.map(eq => (
              <span key={eq.id} className="flex items-center gap-1">
                <span>{eq.emoji || '🔧'}</span>
                <span className="font-medium">{eq.nom}</span>
              </span>
            ))}
            <span className="ml-4 flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500" />Dispo</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500" />Production</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500" />Loue</span>
          </div>

          {/* Grille calendrier */}
          <div className="grid grid-cols-7 gap-0 border-t border-l">
            {JOURS.map((j, i) => (
              <div key={`h-${i}`} className="text-center text-xs font-semibold text-gray-500 py-2 border-b border-r bg-gray-50">{j}</div>
            ))}
            {days.map((day, i) => {
              if (!day) return <div key={`e-${i}`} className="border-b border-r h-20 bg-gray-50/50" />;
              const dateStr = toDateStr(day);
              const isToday = dateStr === todayStr;
              const dow = new Date(year, month, day).getDay();
              const isWeekend = dow === 0 || dow === 6;

              // Calcul statut par equipement
              const eqStatuses = equipements.map(eq => ({
                ...eq,
                status: getEqStatus(eq.id, dateStr),
              }));
              const nbDispo = eqStatuses.filter(e => e.status === 'dispo').length;
              const nbTotal = eqStatuses.length;

              // Locations actives ce jour
              const locsToday = locationsRaw.filter(l => dateStr >= l.date_debut && dateStr <= l.date_fin);

              return (
                <Popover key={`d-${day}`}>
                  <PopoverTrigger asChild>
                    <div
                      className={`border-b border-r h-20 p-1 cursor-pointer transition-colors hover:bg-blue-50 ${isToday ? 'ring-2 ring-blue-500 ring-inset bg-blue-50' : isWeekend ? 'bg-gray-50' : ''}`}
                      data-testid={`day-${dateStr}`}
                    >
                      <div className="flex justify-between items-start">
                        <span className={`text-xs font-medium ${isToday ? 'text-blue-700' : isWeekend ? 'text-gray-400' : 'text-gray-600'}`}>{day}</span>
                        <span className={`text-[10px] font-bold ${nbDispo === nbTotal ? 'text-green-600' : nbDispo === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {nbDispo}/{nbTotal}
                        </span>
                      </div>
                      {/* Pastilles par equipement */}
                      <div className="flex flex-wrap gap-0.5 mt-1">
                        {eqStatuses.map(eq => (
                          <div
                            key={eq.id}
                            className="w-5 h-5 rounded flex items-center justify-center text-[9px]"
                            style={{ backgroundColor: STATUS_COLORS[eq.status] + '30', border: `1.5px solid ${STATUS_COLORS[eq.status]}` }}
                            title={`${eq.emoji || ''} ${eq.nom} — ${eq.status === 'dispo' ? 'Disponible' : eq.status === 'production' ? 'En production' : 'Loue'}`}
                          >
                            {eq.emoji || '🔧'}
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent side="bottom" className="w-72 p-3" onClick={e => e.stopPropagation()}>
                    <p className="font-semibold text-sm mb-2">{formatLocalTime(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00`, { dateOnly: true, weekday: 'long', withoutYear: true })}</p>
                    <div className="space-y-1.5">
                      {eqStatuses.map(eq => {
                        const details = (occupationDetails[eq.id] || {})[dateStr] || [];
                        return (
                          <div key={eq.id} className="text-xs border-b last:border-0 pb-1 last:pb-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{eq.emoji} {eq.nom}</span>
                              <Badge className={`text-[10px] ${eq.status === 'dispo' ? 'bg-green-100 text-green-700' : eq.status === 'production' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                {eq.status === 'dispo' ? 'Dispo' : eq.status === 'production' ? 'Production' : 'Loué'}
                              </Badge>
                            </div>
                            {details.length > 0 && (
                              <ul className="ml-3 mt-0.5 text-[11px] text-slate-600 space-y-0.5">
                                {details.map((det, i) => (
                                  <li key={i} className="truncate">
                                    • {det.type === 'bt'
                                      ? `BT — ${det.dossier_nom || 'Chantier'}`
                                      : `Location — ${det.client_nom || 'Client'}`}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {locsToday.length > 0 && (
                      <div className="mt-2 pt-2 border-t space-y-1">
                        <p className="text-xs font-semibold text-amber-700">Locations actives :</p>
                        {locsToday.map(l => (
                          <div key={l.id} className="flex items-center justify-between text-xs">
                            <span>{l.client_nom || 'Client'} ({l.date_debut} → {l.date_fin})</span>
                            <button onClick={() => handleDeleteLocation(l.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button size="sm" variant="outline" className="w-full mt-2 text-xs" onClick={() => {
                      setLocForm({ equipement_id: '', date_debut: dateStr, date_fin: dateStr, client_nom: '', note: '' });
                      setShowAddDialog(true);
                    }}>
                      <Plus className="w-3 h-3 mr-1" /> Louer ce jour
                    </Button>
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog nouvelle location */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nouvelle location</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Equipement</Label>
              <Select value={locForm.equipement_id} onValueChange={v => setLocForm(f => ({ ...f, equipement_id: v }))}>
                <SelectTrigger data-testid="select-equipement"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {equipements.map(eq => (
                    <SelectItem key={eq.id} value={eq.id}>{eq.emoji} {eq.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Debut</Label><Input type="date" value={locForm.date_debut} onChange={e => setLocForm(f => ({ ...f, date_debut: e.target.value }))} /></div>
              <div><Label className="text-xs">Fin</Label><Input type="date" value={locForm.date_fin} onChange={e => setLocForm(f => ({ ...f, date_fin: e.target.value }))} /></div>
            </div>
            <div><Label className="text-xs">Client</Label><Input value={locForm.client_nom} onChange={e => setLocForm(f => ({ ...f, client_nom: e.target.value }))} placeholder="Nom du client" /></div>
            <div><Label className="text-xs">Note</Label><Input value={locForm.note} onChange={e => setLocForm(f => ({ ...f, note: e.target.value }))} placeholder="Optionnel" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateLocation} disabled={saving}>{saving ? 'Enregistrement...' : 'Confirmer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
